import React, { useMemo } from 'react';
import { TrendingDown, Zap, Building2, Briefcase, Cpu, Globe2 } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../store/stableEmpties';
import { computeFleetDepreciation } from '../../utils/depreciation';
import { computeCapexDepreciation, CAPEX_CATEGORY_LABELS } from '../../utils/capexDepreciation';
import { formatMoney } from '../../utils/format';

const CAPEX_CATEGORY_ICON = {
  depot: Building2,
  office: Briefcase,
  software: Cpu,
  website: Globe2,
};

const CAPEX_CATEGORY_DURATION = {
  depot: '20 ans',
  office: '9 ans',
  software: '3 ans',
  website: '2 ans',
};

/**
 * Affiche le tableau des amortissements par véhicule + agrégats globaux.
 * Inséré dans la page Finance entre la section « Crédits » et le « Bilan ».
 *
 * Source de vérité : `computeFleetDepreciation` (utils/depreciation.js) — la
 * même fonction qui réduit `annualProfit` chaque jour. Garantit que ce que
 * voit l'apprenant à l'écran correspond exactement à l'impact fiscal.
 */
export default function AmortissementsBlock() {
  const vehicles = useGameStore(s => s.vehicles || EMPTY_ARRAY);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const activeOptimizations = useGameStore(s => s.activeOptimizations || EMPTY_OBJECT);
  const annualDepreciation = useGameStore(s => s.annualDepreciation || 0);
  const activeInvestments = useGameStore(s => s.activeInvestments || EMPTY_OBJECT);
  const investmentPurchaseDays = useGameStore(s => s.investmentPurchaseDays || EMPTY_OBJECT);
  const officeUpgrades = useGameStore(s => s.officeUpgrades || EMPTY_OBJECT);

  const dep = useMemo(
    () => computeFleetDepreciation(vehicles, totalDays, activeOptimizations),
    [vehicles, totalDays, activeOptimizations]
  );

  const capex = useMemo(
    () => computeCapexDepreciation({
      totalDays, activeInvestments, investmentPurchaseDays, officeUpgrades,
    }),
    [totalDays, activeInvestments, investmentPurchaseDays, officeUpgrades]
  );

  const ownedCount = dep.items.length;
  const leasedCount = vehicles.filter(v => v.isLeased).length;
  const acceleratedActive = !!activeOptimizations.amortissement_accelere;
  const capexCount = capex.items.length;
  const combinedGross = dep.totalGrossValue + capex.totalGrossValue;
  const combinedVnc = dep.totalVnc + capex.totalVnc;
  const combinedMonthlyDotation = dep.totalMonthlyDotation + capex.totalMonthlyDotation;

  return (
    <div className="bg-ink-800 rounded-lg border border-ink-600 p-4" data-testid="amortissements-block">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-cyan-300" />
          <p className="text-sm font-semibold text-slate-200">Amortissements — flotte &amp; immobilisations</p>
        </div>
        {acceleratedActive && (
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 flex items-center gap-1"
            data-testid="acc-amort-badge"
            title="Mode accéléré actif : 3 ans au lieu de 5 ans"
          >
            <Zap className="w-3 h-3" /> Accéléré · 3 ans
          </span>
        )}
      </div>

      {ownedCount === 0 && capexCount === 0 ? (
        <div className="py-4 text-center" data-testid="amort-empty">
          <p className="text-sm text-slate-500">Aucun véhicule ni CAPEX à amortir.</p>
          {leasedCount > 0 && (
            <p className="text-[10px] text-slate-500 mt-1 italic">
              {leasedCount} véhicule(s) en leasing — non amortissables (charge d'exploitation directe).
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Agrégats globaux — flotte + CAPEX combinés */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="bg-ink-700/60 rounded p-2 text-center" data-testid="amort-total-gross">
              <p className="text-[10px] text-slate-500">Actif brut total</p>
              <p className="text-sm font-bold text-slate-200">{formatMoney(combinedGross)}</p>
            </div>
            <div className="bg-ink-700/60 rounded p-2 text-center" data-testid="amort-total-vnc">
              <p className="text-[10px] text-slate-500">VNC totale</p>
              <p className="text-sm font-bold text-cyan-300">{formatMoney(combinedVnc)}</p>
            </div>
            <div className="bg-ink-700/60 rounded p-2 text-center" data-testid="amort-total-monthly">
              <p className="text-[10px] text-slate-500">Dotation / mois</p>
              <p className="text-sm font-bold text-amber-300">{formatMoney(combinedMonthlyDotation)}</p>
            </div>
          </div>

          {/* ── 1. FLOTTE (5 ans linéaire ou 3 ans accéléré) */}
          {ownedCount > 0 && (
            <div className="mb-4" data-testid="amort-fleet-section">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-mono mb-1.5 flex items-center gap-1.5">
                <span>🚚</span> Flotte — {acceleratedActive ? '3 ans (accéléré)' : '5 ans (linéaire)'}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="amort-table">
                  <thead>
                    <tr className="text-slate-500 text-left">
                      <th className="py-1.5 pr-3 font-normal">Véhicule</th>
                      <th className="py-1.5 px-2 font-normal text-right">Prix d'achat</th>
                      <th className="py-1.5 px-2 font-normal text-right">VNC</th>
                      <th className="py-1.5 px-2 font-normal text-right">Dotation/mois</th>
                      <th className="py-1.5 pl-2 font-normal text-right">Restant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dep.items.map(item => {
                      const ratio = item.price > 0 ? 1 - item.vnc / item.price : 0;
                      return (
                        <tr
                          key={item.vehicleId}
                          className="border-t border-ink-700"
                          data-testid={`amort-row-${item.vehicleId}`}
                        >
                          <td className="py-1.5 pr-3 text-slate-300 truncate max-w-[140px]" title={item.vehicleName}>
                            {item.vehicleName}
                          </td>
                          <td className="py-1.5 px-2 text-right text-slate-400 font-mono">{formatMoney(item.price)}</td>
                          <td className="py-1.5 px-2 text-right text-cyan-300 font-mono">
                            {formatMoney(item.vnc)}
                            <span className="block text-[10px] text-slate-500 font-sans">
                              {Math.round(ratio * 100)} % amorti
                            </span>
                          </td>
                          <td className="py-1.5 px-2 text-right text-amber-300 font-mono">
                            {item.monthlyDotation > 0 ? formatMoney(item.monthlyDotation) : '—'}
                          </td>
                          <td className="py-1.5 pl-2 text-right text-slate-400">
                            {item.remainingMonths > 0
                              ? `${item.remainingMonths} mois`
                              : <span className="text-emerald-400">amorti</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── 2. AUTRES CAPEX (Dépôts 20a, Bureau 9a, Logiciels 3a, Site 2a) */}
          {capexCount > 0 && (
            <div className="mb-3" data-testid="amort-capex-section">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-mono mb-1.5 flex items-center gap-1.5">
                <span>🏛️</span> Autres immobilisations — par catégorie
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(capex.byCategory)
                  .filter(([, slot]) => slot.count > 0)
                  .map(([cat, slot]) => {
                    const Icon = CAPEX_CATEGORY_ICON[cat] || Building2;
                    return (
                      <div
                        key={cat}
                        className="bg-ink-700/40 rounded border border-ink-600 p-2.5"
                        data-testid={`amort-capex-${cat}`}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-1.5">
                            <Icon className="w-3.5 h-3.5 text-cyan-300" />
                            <p className="text-xs font-semibold text-slate-200">
                              {CAPEX_CATEGORY_LABELS[cat]}
                            </p>
                          </div>
                          <span className="text-[10px] font-mono text-slate-500 bg-ink-800 px-1.5 py-0.5 rounded">
                            {CAPEX_CATEGORY_DURATION[cat]}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-1 text-[10px]">
                          <div>
                            <p className="text-slate-500">Actif brut</p>
                            <p className="text-slate-200 font-mono">{formatMoney(slot.grossValue)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">VNC</p>
                            <p className="text-cyan-300 font-mono">{formatMoney(slot.vnc)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Dot/mois</p>
                            <p className="text-amber-300 font-mono">{formatMoney(slot.monthlyDotation)}</p>
                          </div>
                        </div>
                        <ul className="mt-1.5 space-y-0.5">
                          {slot.items.map(it => (
                            <li
                              key={it.id}
                              className="text-[10px] text-slate-400 flex justify-between gap-2"
                              data-testid={`amort-capex-item-${it.id}`}
                            >
                              <span className="truncate" title={it.label}>{it.label}</span>
                              <span className="text-slate-500 font-mono whitespace-nowrap">
                                {it.remainingMonths > 0 ? `${it.remainingMonths}m` : 'amorti'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Impact fiscal — leçon pédagogique */}
          <div className="mt-3 p-2.5 rounded bg-cyan-900/20 border border-cyan-700/30 text-[11px] text-slate-300 leading-relaxed">
            <strong className="text-cyan-300">💡 Impact fiscal&nbsp;:</strong> les dotations aux amortissements
            (flotte + autres CAPEX) réduisent votre bénéfice imposable jour après jour. Cumul depuis le 1er janvier&nbsp;:
            <span className="text-amber-300 font-bold ml-1">{formatMoney(Math.round(annualDepreciation))}</span>
            {acceleratedActive
              ? ' (mode accéléré actif sur la flotte — économie d\'IS plus rapide).'
              : '.'}
          </div>
        </>
      )}
    </div>
  );
}
